/**
 * Utilitários de API para formulários
 */

import envConfig from "../../config/env.js";
import {
  buildAttributionPayload,
  computeAttributionForLoad,
  getDispatchUtms,
  isMundoZeroKmHost,
} from "./attributionUtils.js";
import { getGclidFromUrlOrStorage } from "./gclidTracking.js";

const UTM_CONTENT_BY_MODEL = {
  "form-no-cep": "widget-form-no-cep",
  "form-2-steps": "widget-form-2-steps",
  padrao: "widget-form",
};

let cachedClientIp = null;
let clientIpPromise = null;

/**
 * Resolve o IP público do cliente (cache em memória por sessão).
 * @returns {Promise<string|undefined>}
 */
export async function getClientIpAddress() {
  if (cachedClientIp) return cachedClientIp;
  if (typeof window === "undefined") return undefined;

  if (!clientIpPromise) {
    clientIpPromise = fetch("https://api.ipify.org?format=json")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        cachedClientIp = data?.ip || undefined;
        return cachedClientIp;
      })
      .catch(() => undefined);
  }

  return clientIpPromise;
}

/**
 * Pré-carrega IP do cliente para não atrasar o submit.
 */
export function preloadClientIpAddress() {
  void getClientIpAddress();
}

/**
 * Resolve utmContent com fallback por modelo do formulário.
 * @param {string|undefined|null} utmContent
 * @param {string|undefined|null} formModel
 * @returns {string|undefined}
 */
export function resolveUtmContent(utmContent, formModel) {
  if (utmContent) return utmContent;
  if (!formModel) return undefined;
  return UTM_CONTENT_BY_MODEL[formModel] || "widget-form";
}

/**
 * Envia dados de formulário para o endpoint da API usando uma requisição POST.
 *
 * @async
 * @param {Object} formData - Dados do formulário
 * @param {string} ofertaId - Identificador da oferta associada aos dados enviados
 * @param {Object} utmParams - Parâmetros UTM
 * @param {Object} stepMeta - Metadados de etapa (ex: { stepOne: true, stepTwo: false })
 * @returns {Promise<Object>} Resposta da API
 */
export async function sendFormData(
  formData,
  ofertaId,
  utmParams = {},
  stepMeta = {},
) {
  const isOptInAccepted = (value) =>
    value === true || value === "on" || value === "true" || value === 1;
  const isFilled = (value) =>
    value !== undefined && value !== null && String(value).trim() !== "";
  const setIfFilled = (target, key, value) => {
    if (isFilled(value)) {
      target[key] = value;
    }
  };
  const onlyDigitsOrUndefined = (value) => {
    if (!isFilled(value)) return undefined;
    const digits = String(value).replace(/\D/g, "");
    return digits || undefined;
  };

  const extraData = {};

  if (typeof stepMeta.stepOne === "boolean") {
    extraData.stepOne = stepMeta.stepOne;
  }

  if (typeof stepMeta.stepTwo === "boolean") {
    extraData.stepTwo = stepMeta.stepTwo;
  }

  // Texto aceite -> opt_in_contato_e_ofertas
  if (isOptInAccepted(formData.opt_in_aceite)) {
    extraData.opt_in_contato_e_ofertas = "sim";
  }

  // Texto declaro -> opt_in_terceiros
  if (isOptInAccepted(formData.opt_in_declaro)) {
    extraData.opt_in_terceiros = "sim";
  }

  // Texto estou ciente e opt_in_widget -> opt_in_pyxys
  const optInPyxysValue =
    formData.opt_in_widget !== undefined
      ? formData.opt_in_widget
      : formData.opt_in_ciente;

  if (isOptInAccepted(optInPyxysValue)) {
    extraData.opt_in_pyxys = "sim";
  }

  // gclid — mesmo contrato do widget-form-mundo-zero-km
  const gclid = getGclidFromUrlOrStorage();
  if (gclid) {
    extraData.gclid = gclid;
  }

  // Perguntas LP de Interesse — mesmo contrato do widget-form-mundo-zero-km
  if (
    Array.isArray(formData.modelosInteresse) &&
    formData.modelosInteresse.length > 0
  ) {
    extraData.modelos_interesse = formData.modelosInteresse;
  } else if (isFilled(formData.modelosInteresse)) {
    extraData.modelos_interesse = [formData.modelosInteresse];
  }

  if (isFilled(formData.quandoComprar)) {
    extraData.quando_comprar = formData.quandoComprar;
  }

  if (isFilled(formData.plantingArea)) {
    extraData.planting_area = formData.plantingArea;
  }

  if (isFilled(formData.tractorPower)) {
    extraData.tractor_power = formData.tractorPower;
  }

  const body = {
    campaignIdentifier: ofertaId,
    origem_motor: "widget-form",
  };

  setIfFilled(body, "name", formData.name);
  setIfFilled(body, "email", formData.email);
  setIfFilled(body, "phone", onlyDigitsOrUndefined(formData.phone));
  setIfFilled(body, "cpf", onlyDigitsOrUndefined(formData.cpf));
  setIfFilled(body, "zipCode", onlyDigitsOrUndefined(formData.zipCode));
  setIfFilled(body, "state", formData.state);
  setIfFilled(body, "city", formData.city);
  setIfFilled(body, "birthDate", formData.birthDate);

  if (Object.keys(extraData).length > 0) {
    body.extraData = extraData;
  }

  // UTMs sempre presentes no body — origin/utmSource/utmMedium/utmCampaign
  // nunca ficam vazios ao chegar aqui (fallback "N/A" abaixo), alinhado ao
  // padrão do widget-form-mundo-zero-km (api.js).
  body.origin = utmParams.origin || "widget-form";
  body.utmSource = utmParams.utmSource;
  body.utmMedium = utmParams.utmMedium;
  body.utmCampaign = utmParams.utmCampaign;
  body.utmContent = resolveUtmContent(
    utmParams.utmContent,
    utmParams.formModel,
  );
  body.utmTerm = utmParams.utmTerm;

  if (typeof navigator !== "undefined" && navigator.userAgent) {
    body.userAgent = navigator.userAgent;
  }

  const clientIp = await getClientIpAddress();
  if (clientIp) {
    body.ipAddress = clientIp;
  }

  // Campos sem sinal (ex.: parceiro sem UTM configurada) vão para o motor como "N/A"
  ["utmSource", "utmMedium", "utmCampaign", "utmContent"].forEach((key) => {
    body[key] = body[key] || "N/A";
  });

  const apiUrl = envConfig.get("MOTORDELEADS_API_URL");

  const res = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = await res.json();

  // Retorna objeto com status e dados
  return {
    ok: res.ok,
    status: res.status,
    success: res.ok,
    data: data,
  };
}

/**
 * Envia dados do formulário Minimal Data para o mesmo endpoint do módulo de oferta.
 * Payload:
 * {
 *   email,
 *   campaignIdentifier,
 *   extraData: { categoria, origem }
 * }
 */
export async function sendMinimalDataFormData(payload) {
  const apiUrl = envConfig.get("MOTORDELEADS_API_URL");

  const res = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  return {
    ok: res.ok,
    status: res.status,
    success: res.ok,
    data,
  };
}

/**
 * Monta os parâmetros de UTM/origem para envio — mesma engine de atribuição do
 * widget-form-mundo-zero-km (attributionUtils.js): URL UTM > sinal Google Ads >
 * cookie do portal Next (só dentro de mundozerokm.com.br/subdomínios, leitura,
 * nunca gravação) > config do módulo (tier próprio do widget) > orgânico
 * (calculado só para o envio, nunca persistido). Em sites de parceiros (fora do
 * domínio mundozerokm), usa `getDispatchUtms` — sem cookie/orgânico. Em ambos os
 * casos, `utmCampaign` do tier de config nunca vence o slug real da oferta atual.
 * Usada em todos os fluxos do Oferta Formulário (padrao, form-2-steps, form-no-cep).
 * @param {Object} config - Config do módulo (origin/utmSource/utmMedium/utmCampaign/utmContent/utmTerm)
 * @param {Object} ofertaData - Dados normalizados da oferta (usa `slug` como utmCampaign)
 * @param {Object} [urlUtms] - Resultado de getUtmsFromUrl(); injetável para evitar reler a URL
 * @param {Object} [options] - Opções extras (ex.: formModel)
 * @returns {Object} Parâmetros prontos para sendFormData
 */
export function buildUtmParams(
  config,
  ofertaData,
  urlUtms = getUtmsFromUrl(),
  extraOptions = {},
) {
  const offerSlug = ofertaData?.slug || null;
  const pageUrl = typeof window !== "undefined" ? window.location.href : null;
  const searchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const attributionOptions = {
    utmSource: config.utmSource,
    utmMedium: config.utmMedium,
    utmCampaign: config.utmCampaign,
    utmContent: resolveUtmContent(config.utmContent, extraOptions.formModel),
    utmTerm: config.utmTerm,
  };

  // `|| {}` + `?.` como última linha de defesa: mesmo que algo inesperado
  // faça a engine de atribuição devolver algo não-objeto, a leitura abaixo
  // nunca quebra o envio do formulário.
  const resolved =
    (isMundoZeroKmHost()
      ? buildAttributionPayload(
          computeAttributionForLoad(
            urlUtms,
            attributionOptions,
            searchParams,
            offerSlug,
          ),
          offerSlug,
        )
      : getDispatchUtms(
          urlUtms,
          attributionOptions,
          searchParams,
          offerSlug,
        )) || {};

  return {
    origin: config.origin || "widget-form",
    utmSource: resolved?.utm_source,
    utmMedium: resolved?.utm_medium,
    utmCampaign: resolved?.utm_campaign,
    utmContent: resolveUtmContent(
      resolved?.utm_content,
      extraOptions.formModel,
    ),
    utmTerm: pageUrl || resolved?.utm_term || null,
    formModel: extraOptions.formModel,
  };
}

/**
 * Extrai parâmetros UTM da URL
 * @returns {Object} Objeto com parâmetros UTM
 */
export function getUtmsFromUrl() {
  if (typeof window === "undefined") {
    return {
      utm_source: "",
      utm_medium: "",
      utm_term: "",
      utm_content: "",
      utm_campaign: "",
    };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
    utm_campaign: params.get("utm_campaign") || "",
  };
}
